import * as React from "react";
import { cn } from "../../lib/utils";
import { Surface } from "./Surface";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  featured?: boolean;
  dark?: boolean;
}

/**
 * A card PUBLISHES ITS SURFACE, so descendants that have to look different
 * inside one — a `SubTable`'s header being the case this was added for — do not
 * need to be told. Two channels, because neither covers the other: the React
 * context is synchronous and survives portals, and the `data-phx-surface`
 * attribute is what a DOM probe can see when a descendant is rendered through a
 * boundary the provider does not cross.
 *
 * Purely additive: a card without such a descendant renders exactly the DOM it
 * always did, plus one attribute.
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, featured, dark, ...props }, ref) => (
    <Surface kind="card">
      <div
        ref={ref}
        className={cn(
          "rounded-lg p-space-4 shadow-md border",
          dark
            ? "bg-onyx text-white-pure border-border-dark"
            : "bg-white-pure text-onyx border-border-light",
          featured && "border-t-[2px] border-t-gold",
          className
        )}
        data-phx-surface="card"
        {...props}
      />
    </Surface>
  )
);
Card.displayName = "Card";

export const CardHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-space-1 mb-space-3", className)} {...props} />
);

export const CardTitle = ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3
    className={cn(
      "font-display font-bold text-h3 text-onyx tracking-normal m-0",
      className
    )}
    {...props}
  />
);

export const CardDescription = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn("text-small text-gray-mid", className)} {...props} />
);

export const CardContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("text-body text-onyx leading-normal", className)} {...props} />
);

export const CardFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex items-center justify-between gap-space-3 mt-space-4 pt-space-3",
      "border-t border-border-light",
      className
    )}
    {...props}
  />
);
